import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LifeBuoy, MailCheck, MailWarning, Activity } from "lucide-react";
import { format } from "date-fns";
import { StatCard, SectionHeader, ProgressRow } from "@/components/admin/AdminUI";
import { LoadingState, ErrorState, EmptyState } from "@/components/dashboard/common/PageShell";
import { useEmailHealth, useAdminCounts } from "@/hooks/admin/useAdminMetrics";

const SupportCenter = () => {
  const { data: logs = [], isLoading, error, refetch } = useEmailHealth();
  const { data: counts } = useAdminCounts();

  const stats = useMemo(() => {
    const failed = logs.filter((l) => l.status !== "sent" && l.status !== "delivered");
    const delivered = logs.length - failed.length;
    const templates = new Map<string, number>();
    logs.forEach((l) => templates.set(l.template_name, (templates.get(l.template_name) ?? 0) + 1));
    return {
      total: logs.length,
      delivered,
      failed,
      templates: Array.from(templates, ([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count),
    };
  }, [logs]);

  if (isLoading) return <LoadingState label="Loading operations data…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Support & operations"
        description="Delivery health, inbound requests and platform service signals."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Emails logged" value={stats.total} icon={MailCheck} />
        <StatCard
          label="Delivery failures"
          value={stats.failed.length}
          icon={MailWarning}
          tone={stats.failed.length > 0 ? "danger" : "positive"}
        />
        <StatCard label="Open consultations" value={counts?.consultations ?? 0} icon={LifeBuoy} />
        <StatCard label="Payout requests" value={counts?.payout_requests ?? 0} icon={Activity} />
      </div>

      <Tabs defaultValue="delivery" className="space-y-4">
        <TabsList>
          <TabsTrigger value="delivery">Email delivery</TabsTrigger>
          <TabsTrigger value="failures">Failures</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="delivery" className="space-y-2">
          {logs.length === 0 ? (
            <EmptyState icon={MailCheck} title="No emails yet" description="Transactional sends appear here." />
          ) : (
            logs.slice(0, 40).map((l) => (
              <Card key={l.id} className="bg-card border-border">
                <CardContent className="flex flex-wrap items-center gap-3 p-3">
                  <span className="flex-1 min-w-[200px] text-sm text-foreground">{l.recipient_email}</span>
                  <span className="text-xs text-muted-foreground">{l.template_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(l.created_at), "dd MMM HH:mm")}
                  </span>
                  <Badge
                    className={`border-0 capitalize ${
                      l.status === "sent" || l.status === "delivered" ? "bg-emerald-600" : "bg-red-600"
                    }`}
                  >
                    {l.status}
                  </Badge>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="failures" className="space-y-2">
          {stats.failed.length === 0 ? (
            <EmptyState icon={MailCheck} title="No failures" description="Every logged send was accepted by the provider." />
          ) : (
            stats.failed.map((l) => (
              <Card key={l.id} className="border-red-500/30 bg-card">
                <CardContent className="p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium text-foreground">{l.recipient_email}</span>
                    <Badge className="border-0 bg-red-600 capitalize">{l.status}</Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {l.template_name} · {l.error_message ?? "No provider message"}
                  </p>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="templates">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base text-foreground">Send volume by template</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {stats.templates.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing sent yet.</p>
              ) : (
                stats.templates.map((t) => (
                  <ProgressRow key={t.name} label={t.name} value={t.count} total={stats.total || 1} />
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SupportCenter;
